import { useCallback, useEffect, useRef, useState } from 'react'
import { decoupledDelete } from '../../../services/api'

/**
 * Data access for the builder's médiathèque (`/api/builder/media/*`).
 *
 * One media = one Drupal `media` entity of bundle `image`, carrying the
 * dynamic-crop descriptor (`{src, scheme, cropBase}`) so a consumer asks the
 * crop endpoint for the exact box it needs instead of downloading the
 * original. Vector sources arrive without that descriptor — there is nothing
 * to resize in an SVG — so consumers fall back on `url`.
 *
 * Item shape:
 *   {mid, name, filename, mime, size, created, alt, url, width, height,
 *    categories: [{id, label}], sites: [{id, label}], editable, deletable,
 *    src?, scheme?, cropBase?}
 *
 * The search term is debounced here rather than in the component: the pop-in
 * refetches on every keystroke otherwise, and the category rail would flicker
 * with it.
 */

const PAGE_SIZE = 60
const SEARCH_DEBOUNCE_MS = 250

/**
 * Les catégories passent par l'API GÉNÉRIQUE de decoupled_toolbox, pas par un
 * point d'API maison : c'est le moteur prévu pour lire et écrire une entité
 * Drupal sans contrôleur. Le display `decoupled` du vocabulaire expose `id` et
 * `name`, et porte `decoupled_write_access` pour la création.
 *
 * ⚠️ L'opérateur de filtre est la clé `c` (et non `op`) :
 * `filter[0][f]=name&filter[0][v]=…&filter[0][c]=CONTAINS`.
 */
const CATEGORY_COLLECTION = '/decoupled-api/taxonomy_term/media_category/collection'
const CATEGORY_SAVE = '/decoupled-api/save?display=decoupled&entity_type=taxonomy_term&bundle=media_category'
const CATEGORY_LIMIT = 20

/**
 * Un fichier que la médiathèque accepte de téléverser.
 *
 * Le type MIME du navigateur suffit presque toujours, MAIS il peut être vide :
 * un `.ico` n'est associé à aucun type sur certains systèmes, et le fichier
 * disparaissait alors de la sélection sans le moindre message. On retombe donc
 * sur l'extension — le serveur, lui, tranche sur le CONTENU (cf.
 * OrbitMediaController::MIME_EXTENSIONS).
 */
const UPLOADABLE_EXTENSIONS = /\.(jpe?g|png|gif|webp|avif|svg|ico)$/i
const isImageFile = (f) => f.type.startsWith('image/') || UPLOADABLE_EXTENSIONS.test(f.name ?? '')

const jsonPost = async (url, body) => {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  })
  const data = await res.json().catch(() => ({}))
  // Deux conventions cohabitent : `{success, message}` pour les points
  // d'API de la médiathèque, `{status: {type, message}}` pour l'API
  // générique de decoupled_toolbox.
  if (!res.ok || data.success === false || data.status?.type === 'error') {
    throw new Error(data.message || data.status?.message || `Erreur ${res.status}`)
  }
  return data
}

/**
 * @param {object} [options]
 *   - initialSite : le site sur lequel ouvrir la médiathèque (le site de la
 *     page en cours d'édition). La grille démarre filtrée dessus.
 *   - uploadSite  : le site auquel rattacher ce qui est chargé. Sans lui, une
 *     image déposée depuis un site filtré disparaîtrait aussitôt de la grille.
 */
export const useMediaLibrary = ({ initialSite = null, uploadSite = null } = {}) => {
  const [items, setItems] = useState([])
  const [categories, setCategories] = useState([])
  const [sites, setSites] = useState([])
  const [total, setTotal] = useState(0)
  const [libraryTotal, setLibraryTotal] = useState(0)
  const [page, setPage] = useState(0)

  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [category, setCategory] = useState(null)
  const [site, setSite] = useState(initialSite)

  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)

  // Guards a slow response from overwriting a newer one (typing fast in the
  // search field fires overlapping requests).
  const requestRef = useRef(0)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [search])

  // A new search, category or site always restarts at the first page.
  useEffect(() => { setPage(0) }, [debouncedSearch, category, site])

  const fetchPage = useCallback(async (targetPage, { append = false } = {}) => {
    const ticket = ++requestRef.current
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), page: String(targetPage) })
      if (debouncedSearch) params.set('q', debouncedSearch)
      if (category !== null) params.set('category', String(category))
      if (site !== null) params.set('site', String(site))

      const res = await fetch(`/api/builder/media?${params}`, { credentials: 'include' })
      if (!res.ok) throw new Error(`Erreur ${res.status}`)
      const data = await res.json()
      if (ticket !== requestRef.current) return

      setItems(prev => (append ? [...prev, ...(data.items ?? [])] : (data.items ?? [])))
      setCategories(data.categories ?? [])
      setSites(data.sites ?? [])
      setTotal(data.total ?? 0)
      setLibraryTotal(data.libraryTotal ?? data.total ?? 0)
    } catch (err) {
      if (ticket !== requestRef.current) return
      setError(err.message)
      if (!append) setItems([])
    } finally {
      if (ticket === requestRef.current) setLoading(false)
    }
  }, [debouncedSearch, category, site])

  useEffect(() => { fetchPage(0) }, [fetchPage])

  const loadMore = useCallback(() => {
    const next = page + 1
    setPage(next)
    fetchPage(next, { append: true })
  }, [page, fetchPage])

  /**
   * Re-read just the rail. Counts are library-wide, so they change on every
   * upload / delete / re-filing regardless of the current filters.
   */
  const refreshCategories = useCallback(async () => {
    try {
      const res = await fetch('/api/builder/media?limit=1', { credentials: 'include' })
      if (!res.ok) return
      const data = await res.json()
      setCategories(data.categories ?? [])
      setSites(data.sites ?? [])
    } catch {
      // A stale count is harmless — leave the rail as it is.
    }
  }, [])

  /**
   * Upload one or more files. Returns the items actually created, so the
   * caller can select the last one. Files are sent one at a time: the
   * endpoint takes a single file, and a sequential loop keeps the error
   * message attached to the file that caused it.
   */
  const upload = useCallback(async (files, { categories: assignTo } = {}) => {
    const list = Array.from(files ?? []).filter(isImageFile)
    if (!list.length) return []

    setUploading(true)
    setError(null)
    const created = []
    try {
      for (const file of list) {
        const form = new FormData()
        form.append('file', file)
        if (assignTo?.length) form.append('categories', assignTo.join(','))
        if (uploadSite !== null) form.append('siteId', String(uploadSite))

        const res = await fetch('/api/builder/media/upload', {
          method: 'POST',
          credentials: 'include',
          body: form,
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok || !data.success) {
          throw new Error(`${file.name} : ${data.message || `erreur ${res.status}`}`)
        }
        created.push(data.item)
      }
      if (created.length) {
        setItems(prev => [...created, ...prev])
        setTotal(prev => prev + created.length)
        setLibraryTotal(prev => prev + created.length)
        // The rail counts moved — refresh them without disturbing the grid.
        refreshCategories()
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setUploading(false)
    }
    return created
  }, [refreshCategories, uploadSite])

  /** Rename / retitle / re-file one media. */
  const updateItem = useCallback(async (mid, patch) => {
    setError(null)
    try {
      const data = await jsonPost('/api/builder/media/update', { mid, ...patch })
      setItems(prev => prev.map(item => (item.mid === mid ? data.item : item)))
      if ('categories' in patch) refreshCategories()
      return data.item
    } catch (err) {
      setError(err.message)
      return null
    }
  }, [refreshCategories])

  const removeItem = useCallback(async (mid) => {
    setError(null)
    try {
      await decoupledDelete('media', 'image', mid)
      setItems(prev => prev.filter(item => item.mid !== mid))
      setTotal(prev => Math.max(0, prev - 1))
      setLibraryTotal(prev => Math.max(0, prev - 1))
      refreshCategories()
      return true
    } catch (err) {
      setError(err.message)
      return false
    }
  }, [refreshCategories])

  /**
   * Autocomplétion des catégories, côté serveur.
   *
   * Le vocabulaire ne voyage plus dans chaque réponse de listage : on
   * interroge un point de terminaison dédié à la frappe. Renvoie [] plutôt
   * que de lever — une suggestion manquante ne doit pas casser la saisie.
   */
  const searchCategories = useCallback(async (q, { signal } = {}) => {
    try {
      const params = new URLSearchParams({
        display: 'decoupled',
        sort: 'name',
        range: String(CATEGORY_LIMIT),
      })
      // Un `v` vide est REFUSÉ par le filtre (il teste `empty()`) : sans
      // saisie, on n'envoie simplement pas de condition.
      if (q) {
        params.set('filter[0][f]', 'name')
        params.set('filter[0][v]', q)
        params.set('filter[0][c]', 'CONTAINS')
      }
      const res = await fetch(`${CATEGORY_COLLECTION}?${params}`, { credentials: 'include', signal })
      if (!res.ok) return []
      const collection = await res.json()
      // Chaque entrée est un « Syncable Object » : la donnée est sous `data`.
      return (Array.isArray(collection) ? collection : [])
        .map(entry => entry?.data)
        .filter(d => d && d.id != null)
        .map(d => ({ id: Number(d.id), label: String(d.name ?? '') }))
    } catch {
      return []
    }
  }, [])

  /**
   * Crée une catégorie par l'API générique.
   *
   * ⚠️ Deux détails du contrat : le corps attend une enveloppe `{data: {…}}`,
   * et l'id de l'entité créée revient à la RACINE de la réponse, pas dans
   * `data`.
   */
  const createCategory = useCallback(async (label) => {
    setError(null)
    try {
      const saved = await jsonPost(CATEGORY_SAVE, { data: { name: label } })
      if (saved.id == null) {
        throw new Error(saved.status?.message || 'Création refusée.')
      }
      const category = { id: Number(saved.id), label, count: 0 }
      setCategories(prev => (
        prev.some(c => c.id === category.id)
          ? prev
          : [...prev.filter(c => c.id !== 0), category, ...prev.filter(c => c.id === 0)]
      ))
      return category
    } catch (err) {
      setError(err.message)
      return null
    }
  }, [])

  return {
    items,
    categories,
    sites,
    total,
    libraryTotal,
    loading,
    uploading,
    error,
    clearError: () => setError(null),
    hasMore: items.length < total,
    loadMore,
    search,
    setSearch,
    category,
    setCategory,
    site,
    setSite,
    upload,
    updateItem,
    removeItem,
    createCategory,
    searchCategories,
    reload: () => fetchPage(0),
  }
}
